import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"
import {
  useGetProspectQuery,
  useSetProspectContactPreferencesMutation,
} from "@apis/prospects/useProspects"
import type { ISetProspectContactPreferencesRequest } from "@apis/prospects/types"
import { Switch } from "@components/ui/switch"
import { prospectsKeys } from "@apis/prospects/queryKeys"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select"
import { useGetContactLanguagesQuery } from "@apis/shared/useShared"

type TContactPreferenceField =
  | "can_call"
  | "can_email"
  | "can_knock"
  | "can_mail"
  | "can_text"
  | "language_preference"

type TAccountContactPreferencesFormProps = {
  prospect_guid: string | undefined
  visibleFields?: TContactPreferenceField[]
}

export const AccountContactPreferencesForm = ({
  prospect_guid,
  visibleFields,
}: TAccountContactPreferencesFormProps) => {
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()

  const getProspectQuery = useGetProspectQuery(prospect_guid)
  const getContactLanguagesQuery = useGetContactLanguagesQuery()
  const setProspectContactPreferencesMutation =
    useSetProspectContactPreferencesMutation()

  const form = useForm<ISetProspectContactPreferencesRequest>({
    mode: "onTouched",
    defaultValues: {
      can_call: getProspectQuery.data?.can_call || false,
      can_email: getProspectQuery.data?.can_email || false,
      can_knock: getProspectQuery.data?.can_knock || false,
      can_mail: getProspectQuery.data?.can_mail || false,
      can_text: getProspectQuery.data?.can_text || false,
      language_preference: getProspectQuery.data?.language_preference || "",
    },
  })

  const shouldShowField = (field: TContactPreferenceField): boolean => {
    return !visibleFields || visibleFields.includes(field)
  }

  const buildRequestData = (
    data: ISetProspectContactPreferencesRequest,
  ): Partial<ISetProspectContactPreferencesRequest> => {
    if (!visibleFields) return data

    return visibleFields.reduce((acc, key) => {
      return {
        ...acc,
        [key]: data[key as keyof ISetProspectContactPreferencesRequest],
      }
    }, {} as Partial<ISetProspectContactPreferencesRequest>)
  }

  const handleSubmit = (data: ISetProspectContactPreferencesRequest) => {
    setProspectContactPreferencesMutation.mutate(
      {
        prospect_guid: prospect_guid as string,
        request: buildRequestData(data),
      },
      {
        onSuccess: () => {
          toast.success("Successfully updated account contact preferences!")
          queryClient.invalidateQueries({
            queryKey: prospectsKeys.detail(prospect_guid),
          })
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error updating account contact preferences! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
      >
        {shouldShowField("can_call") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="can_call"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Can Call</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        {shouldShowField("can_email") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="can_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Can Email</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        {shouldShowField("can_knock") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="can_knock"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Can Knock</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        {shouldShowField("can_mail") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="can_mail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Can Mail</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        {shouldShowField("can_text") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="can_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Can Text</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        {shouldShowField("language_preference") && (
          <div className="tw:col-span-6 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="language_preference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Language Preference</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl className="tw:w-full tw:min-w-0">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Language Preference" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {getContactLanguagesQuery.data?.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="tw:col-span-12 tw:flex tw:justify-end">
          <Button
            type="submit"
            disabled={
              setProspectContactPreferencesMutation.isPending ||
              // !form.formState.isDirty || for some reason this is buggy with the switch component
              !form.formState.isValid
            }
          >
            {setProspectContactPreferencesMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Contact Preferences</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
